import {
    AVAILABLE_LANGUAGES,
    NEXT_APP_MAINTENANCE_DISABLE_BOOKING,
} from '@/constants';
import {
    AppDispatch,
    DisplayedCarInfoInterface,
    ExperimentShowFinancingButton,
    isReserveHomeAllowed,
    isReserveHubAllowed,
    isSeeDealer0Allowed,
    isSeeHomeAllowed,
    isSeeHubAllowed,
} from '@/types';
import { NotifyMeButton } from './CarProfileNew';
import SylndrButton from '../shared/Button/button-styled.component';
import Typography from '../shared/typography/typography.component';
import Link from '../WrappedLink';
import { useAnalytics } from '@/integrations';
import { toggleLoading } from '@/redux/reducers/checkoutReducer';
import { useDispatch } from 'react-redux';
import { TFunction } from 'i18next';
import { DownloadApp } from '@/components/download-app';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useEffect, useState } from 'react';

const MobileButtons = ({
    carDetails,
    carInfo,

    t,
    lang,
}: {
    carDetails: any;
    carInfo: DisplayedCarInfoInterface;
    t: TFunction<'translation', undefined>;
    lang: AVAILABLE_LANGUAGES;
}) => {
    const { trackEvent } = useAnalytics();
    const dispatch = useDispatch<AppDispatch>();

    const FEATURE_FLAG_SHOW_FINANCING_BUTTON = 'show-financing-button';

    const showFinancingButtonExperiment = useFeatureValue(
        FEATURE_FLAG_SHOW_FINANCING_BUTTON,
        ExperimentShowFinancingButton.ORIGINAL
    );
    const [showFinancingButton, setShowFinancingButton] = useState<boolean>();

    useEffect(() => {
        if (showFinancingButtonExperiment) {
            setShowFinancingButton(
                showFinancingButtonExperiment !==
                    ExperimentShowFinancingButton.HIDE
            );
        }
    }, [showFinancingButtonExperiment]);

    if (carDetails?.isSold || carDetails?.isPurchaseInProgress) {
        return (
            <div
                className="md:hidden"
                style={{
                    position: 'sticky',
                    bottom: '0px',
                    background: 'white',
                    padding: '16px 12px',
                    boxShadow: '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
                    borderTop: '1px solid #EAECF0',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    height: '98px',
                }}
            ></div>
        );
    }

    const isReserveAllowed =
        carInfo?.bookingType &&
        (isReserveHubAllowed(carInfo.bookingType) ||
            isReserveHomeAllowed(carInfo.bookingType));

    const isSeeAllowed =
        carInfo?.bookingType &&
        (isSeeHomeAllowed(carInfo.bookingType) ||
            isSeeHubAllowed(carInfo.bookingType) ||
            isSeeDealer0Allowed(carInfo.bookingType));

    const handleReserveClick = () => {
        trackEvent('Action: Reserve Car', {
            carName: carDetails?.sfVehicleName,
            isRefurbished: carDetails?.isRefurbished,
            newlyAdded:
                carDetails?.isNewlyAdded &&
                !(carDetails.isReserved || carDetails.isPurchaseInProgress),
            status: carDetails?.isPurchaseInProgress
                ? 'Being Sold'
                : carDetails?.isReserved
                ? 'Reserved'
                : 'Not Reserved',
        });
        dispatch(toggleLoading(false));
    };

    const handleSeeClick = () => {
        trackEvent('Action: Book Visit', {
            carName: carDetails?.sfVehicleName,
            isRefurbished: carDetails?.isRefurbished,
            newlyAdded:
                carDetails?.isNewlyAdded &&
                !(carDetails.isReserved || carDetails.isPurchaseInProgress),
            status: carDetails?.isPurchaseInProgress
                ? 'Being Sold'
                : carDetails?.isReserved
                ? 'Reserved'
                : 'Not Reserved',
        });
        dispatch(toggleLoading(false));
    };

    return (
        <div
            style={{
                position: 'sticky',
                bottom: '0px',
                background: 'white',
                padding: '16px 12px',
                boxShadow: '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
                borderTop: '1px solid #EAECF0',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                height: '98px',
            }}
            className={'md:hidden'}
        >
            {carDetails?.isReserved && carInfo?.id ? (
                <NotifyMeButton carId={carInfo.id} />
            ) : isReserveAllowed ? (
                <Link
                    disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                    href={`/${lang}/car-details/${carInfo.id}/checkout/reserve`}
                    style={{
                        textDecoration: 'none',
                        width: '100%',
                        height: '80%',
                    }}
                >
                    <SylndrButton
                        onClick={handleReserveClick}
                        style={{ height: '100%' }}
                        disabled={
                            carDetails?.isReserved ||
                            NEXT_APP_MAINTENANCE_DISABLE_BOOKING
                        }
                        variant="primary"
                    >
                        <Typography variant="body1SemiBold">
                            {t('reserve car')}
                        </Typography>
                    </SylndrButton>
                </Link>
            ) : null}

            {carInfo?.id && isSeeAllowed ? (
                <Link
                    disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                    href={`/${lang}/car-details/${carInfo.id}/checkout/see`}
                    style={{
                        textDecoration: 'none',
                        width: '100%',
                        height: '80%',
                    }}
                >
                    <SylndrButton
                        onClick={handleSeeClick}
                        style={{ height: '100%' }}
                        disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                        variant={
                            !isReserveAllowed || carInfo.isReserved
                                ? 'primary'
                                : 'secondary'
                        }
                    >
                        <Typography variant="body2SemiBold">
                            {t('See Car')}
                        </Typography>
                    </SylndrButton>
                </Link>
            ) : null}
            {showFinancingButton &&
            carInfo?.id &&
            (carInfo?.bookingType as number) < 4 &&
            !carInfo.isReserved &&
            !carInfo.isSold &&
            !carInfo.isPurchaseInProgress &&
            (isSeeHomeAllowed(carInfo?.bookingType) ||
                isSeeHubAllowed(carInfo?.bookingType)||
                isSeeDealer0Allowed(carInfo?.bookingType)) ? (
                <div style={{ width: '100%', height: '80%' }}>
                    <DownloadApp
                        text={t('financing')}
                        deepLink={`CarProfileScreen/${carDetails.id}`}
                        source="Main Financing CTA in Car Profile"
                        textVariant="body2SemiBold"
                        buttonProps={{
                            variant: 'swift',
                        }}
                    />
                </div>
            ) : (
                <></>
            )}
        </div>
    );
};
export default MobileButtons;
